import { FC } from "react";
import { PersonInformation } from "@/types";
import { RelationshipToPrimaryOwner } from "@/types/enums";
import { useJointAccountOwner } from "@/hooks/useJointAccountOwner";
import { getFullName } from "../ComplexFields/PersonInformationComplexField";
import { SensitiveValueComponent } from "../SensitiveValueComponent/SensitiveValueComponent";
import "./JointAccountOwners.scss";

export interface JointAccountOwnerTableRowProps {
  financialApplicationId: string;
  jointOwnerId: string;
  onDelete: (jointAccountId: string) => void;
  validationErrors: Record<string, string[]>;
  readOnly?: boolean;
}

export const JointAccountOwnerTableRow: FC<JointAccountOwnerTableRowProps> = (props) => {
  const { formValue } = useJointAccountOwner(props.financialApplicationId, props.jointOwnerId);

  if (!formValue) {
    return (
      <tr>
        <td colSpan={6}>Loading...</td>
      </tr>
    );
  }

  const personInfo = formValue.personInformation as PersonInformation;
  const fullName = getFullName(personInfo) || "New Joint Account Owner";

  // Get relationship text
  const getRelationshipText = (relationship?: RelationshipToPrimaryOwner): string => {
    if (!relationship) return "";

    const relationshipMap: Record<RelationshipToPrimaryOwner, string> = {
      [RelationshipToPrimaryOwner.Spouse]: "Spouse",
      [RelationshipToPrimaryOwner.DomesticPartner]: "Domestic Partner",
      [RelationshipToPrimaryOwner.Parent]: "Parent",
      [RelationshipToPrimaryOwner.LegalGuardian]: "Legal Guardian",
      [RelationshipToPrimaryOwner.Child]: "Child",
      [RelationshipToPrimaryOwner.Sibling]: "Sibling",
      [RelationshipToPrimaryOwner.OtherRelative]: "Other Relative",
      [RelationshipToPrimaryOwner.BusinessPartnerOrCoOwner]: "Business Partner/Co-Owner",
      [RelationshipToPrimaryOwner.AccountantOrBookkeeper]: "Accountant/Bookkeeper",
      [RelationshipToPrimaryOwner.Employee]: "Employee",
      [RelationshipToPrimaryOwner.TrusteeOrPowerOfAttorney]: "Trustee/Power of Attorney",
      [RelationshipToPrimaryOwner.Friend]: "Friend",
      [RelationshipToPrimaryOwner.Other]: "Other",
    };

    return relationshipMap[relationship] || "";
  };

  // Get address text (truncated)
  const getAddressText = (): string => {
    if (!personInfo?.homeAddress?.street) return "";
    return personInfo.homeAddress.street.length > 15
      ? `${personInfo.homeAddress.street.substring(0, 15)}...`
      : personInfo.homeAddress.street;
  };

  // Get account types (placeholder for now)
  const getAccountTypes = (): string => {
    return "Personal Checking,\nPersonal Money Market";
  };

  return (
    <tr className="joint-account-owners-table__row">
      <td className="joint-account-owners-table__cell joint-account-owners-table__cell--name">
        {fullName}
      </td>
      <td className="joint-account-owners-table__cell joint-account-owners-table__cell--account">
        {getAccountTypes()}
      </td>
      <td className="joint-account-owners-table__cell joint-account-owners-table__cell--ssn">
        {personInfo?.socialSecurityNumber && (
          <SensitiveValueComponent className="joint-account-owners-table__ssn-component">
            {personInfo.socialSecurityNumber}
          </SensitiveValueComponent>
        )}
      </td>
      <td className="joint-account-owners-table__cell joint-account-owners-table__cell--relationship">
        {getRelationshipText(formValue.relationshipToPrimary)}
      </td>
      <td className="joint-account-owners-table__cell joint-account-owners-table__cell--address">
        {getAddressText()}
      </td>
      <td className="joint-account-owners-table__cell joint-account-owners-table__cell--phone">
        {personInfo?.phoneNumber || ""}
      </td>
    </tr>
  );
};
